package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import br.com.htms.inspeccaocampo.data.efetivo.Datas
import br.com.htms.inspeccaocampo.data.ocorrencias.Ocorrencia
import br.com.htms.inspeccaocampo.data.ocorrencias.OcorrenciasDados
import br.com.htms.inspeccaocampo.data.ocorrencias.OcDatas
import br.com.htms.inspeccaocampo.data.ocorrencias.digitoClasseOc
import br.com.htms.inspeccaocampo.data.ocorrencias.nomeClasseOc
import br.com.htms.inspeccaocampo.ui.kit.CoresDash
import br.com.htms.inspeccaocampo.ui.kit.DashDatas
import br.com.htms.inspeccaocampo.ui.kit.FatiaDash
import br.com.htms.inspeccaocampo.ui.kit.FiltroPeriodo
import br.com.htms.inspeccaocampo.ui.kit.FiltrosAtivos
import br.com.htms.inspeccaocampo.ui.kit.FmtDash
import br.com.htms.inspeccaocampo.ui.kit.GradeKpis
import br.com.htms.inspeccaocampo.ui.kit.GraficoColunas
import br.com.htms.inspeccaocampo.ui.kit.GraficoRosca
import br.com.htms.inspeccaocampo.ui.kit.ItemPainel
import br.com.htms.inspeccaocampo.ui.kit.KpiDash
import br.com.htms.inspeccaocampo.ui.kit.PainelDashboard
import br.com.htms.inspeccaocampo.ui.kit.PaineisDoDash
import br.com.htms.inspeccaocampo.ui.kit.PeriodoDash
import br.com.htms.inspeccaocampo.ui.kit.RankingBarras
import br.com.htms.inspeccaocampo.ui.kit.SemDadosDash
import br.com.htms.inspeccaocampo.ui.theme.PortalCores

private fun numero(s: String): Double = s.replace(",", ".").trim().toDoubleOrNull() ?: 0.0

@Composable
fun DashboardOcorrencias(d: OcorrenciasDados, carregando: Boolean = false, onAtualizar: (() -> Unit)? = null) {
    var periodo by remember { mutableStateOf(PeriodoDash.ANO) }
    var fTipo by remember { mutableStateOf<String?>(null) }
    var fObra by remember { mutableStateOf<String?>(null) }
    var fEmpresa by remember { mutableStateOf<String?>(null) }
    var fClasse by remember { mutableStateOf<String?>(null) }
    var fCarac by remember { mutableStateOf<String?>(null) }
    var fTipico by remember { mutableStateOf<String?>(null) }
    var fFaixa by remember { mutableStateOf<Int?>(null) }

    val hoje = remember { OcDatas.hoje() }
    fun nomeTipo(o: Ocorrencia) = d.tipo(o.prefixo)?.nome ?: o.tipo.ifBlank { "Sem tipo" }
    fun classe(o: Ocorrencia) = nomeClasseOc(o.campo("classificacao"))
    fun obra(o: Ocorrencia) = o.campo("obra").ifBlank { "Obra não informada" }
    fun empresa(o: Ocorrencia) = o.campo("empresa").ifBlank { "Não informada" }
    fun tipico(o: Ocorrencia) = o.campo("tipoAcid").ifBlank { "Não informado" }
    fun ehAcidenteDe(o: Ocorrencia) = o.ehAcidenteAtual(nomeTipo(o))

    val validas = d.ocorrencias.filter { Datas.valida(it.data) }
    val faixas = remember(periodo, d) { DashDatas.faixas(periodo, validas.minOfOrNull { it.data }, hoje) }
    val noPeriodo = validas.filter { DashDatas.noPeriodo(it.data, periodo, hoje) && it.data <= hoje }
    val semFaixa = noPeriodo.filter { o ->
        (fTipo == null || nomeTipo(o) == fTipo) &&
            (fObra == null || obra(o) == fObra) &&
            (fEmpresa == null || empresa(o) == fEmpresa) &&
            (fClasse == null || classe(o) == fClasse) &&
            (fCarac == null || o.marcasCaracterizacao().any { it.equals(fCarac, true) }) &&
            (fTipico == null || tipico(o) == fTipico)
    }
    val filtradas = semFaixa.filter { fFaixa == null || DashDatas.indice(faixas, it.data) == fFaixa }
    val idsFiltrados = filtradas.map { it.id }.toSet()
    val acoes = d.acoes.filter { it.ocId in idsFiltrados }
    val acidentes = filtradas.filter { ehAcidenteDe(it) }
    val comAfastamento = filtradas.count { it.comAfastamento() }
    val foraPrazo = filtradas.count { it.investigacaoForaDoPrazo(hoje) }
    val ultimaAcidente = validas.filter { ehAcidenteDe(it) && it.data <= hoje }.maxOfOrNull { it.data }
    val diasSem = ultimaAcidente?.let { Datas.diff(hoje, it) }
    val vencidas = acoes.count { it.vencida(hoje) }
    val abertas = acoes.count { !it.concluida }

    val porTipo = contagem(filtradas, ::nomeTipo)
    val cores = coresRanking(contagem(noPeriodo, ::nomeTipo).map { it.first })

    val filtrosAtivos = buildList {
        fFaixa?.let { i -> faixas.getOrNull(i)?.let { add("Período: ${it.rotulo}" to { fFaixa = null }) } }
        fTipo?.let { add("Tipo: $it" to { fTipo = null }) }
        fCarac?.let { add(it to { fCarac = null }) }
        fClasse?.let { add(it to { fClasse = null }) }
        fTipico?.let { add("Típico/Trajeto: $it" to { fTipico = null }) }
        fObra?.let { add("Obra: $it" to { fObra = null }) }
        fEmpresa?.let { add("Cliente: $it" to { fEmpresa = null }) }
    }

    PainelDashboard(
        titulo = "Dashboard — Gestão de Ocorrências",
        subtitulo = "${periodo.rotulo} · ${FmtDash.qtd(filtradas.size)} ocorrência(s)" +
            (diasSem?.let { " · $it dia(s) sem acidente" } ?: ""),
        carregando = carregando,
        onAtualizar = onAtualizar,
        camposCor = listOf(
            "Acidentes" to 0xFFC62828,
            "Classe 0" to 0xFF8A94A6,
            "Classe 1" to 0xFFE0A000,
            "Classe 2" to 0xFFE07B00,
            "Classe 3" to 0xFF6A1B9A,
            "Classe 4" to 0xFFC62828,
            "Classe 5" to 0xFF6D1212,
            "Concluídas" to 0xFF1B7F3B,
            "No prazo" to 0xFFE0A000,
            "Vencidas" to 0xFFC62828
        ) + cores.keys.mapIndexed { i, k -> k to CoresDash.corLong(i) },
        filtros = {
            FiltroPeriodo(periodo, { periodo = it; fFaixa = null })
            FiltrosAtivos(filtrosAtivos) {
                fTipo = null; fObra = null; fEmpresa = null; fClasse = null; fCarac = null; fTipico = null; fFaixa = null
            }
        }
    ) { largo, grande ->
        if (d.ocorrencias.isEmpty()) {
            SemDadosDash("Sem ocorrências", "Nenhuma ocorrência registrada ainda.")
            return@PainelDashboard
        }
        PaineisDoDash("ocorrencias", largo, organizar = !grande, itens = listOf(
            ItemPainel("kpis", "Indicadores", inteiro = true, conteudo = {
                GradeKpis(
            largo,
            listOf(
                { m -> KpiDash("Ocorrências", FmtDash.qtd(filtradas.size), m, legenda = periodo.rotulo) },
                { m ->
                    KpiDash(
                        "Acidentes", FmtDash.qtd(acidentes.size), m,
                        cor = if (acidentes.isEmpty()) CoresDash.bom else CoresDash.ruim,
                        legenda = "$comAfastamento com afastamento",
                        destaque = acidentes.isNotEmpty()
                    )
                },
                { m ->
                    val meta = d.meta
                    val ok = diasSem != null && meta != null && diasSem >= meta
                    KpiDash(
                        "Dias sem acidente", diasSem?.toString() ?: "—", m,
                        cor = if (meta == null) PortalCores.Navy else if (ok) CoresDash.bom else CoresDash.atencao,
                        legenda = meta?.let { "meta: $it dias" } ?: "desde o último acidente"
                    )
                },
                { m ->
                    val dias = filtradas.sumOf { numero(it.campo("diasAfast")) }
                    KpiDash("Dias de afastamento", FmtDash.qtd(dias), m, cor = if (dias > 0) CoresDash.ruim else PortalCores.Navy, legenda = "debitados: ${FmtDash.qtd(filtradas.sumOf { numero(it.campo("diasDeb")) })}")
                },
                { m ->
                    val pend = filtradas.count { !it.investigacaoConcluida }
                    KpiDash(
                        "Investigações abertas", FmtDash.qtd(pend), m,
                        cor = if (foraPrazo > 0) CoresDash.ruim else if (pend > 0) CoresDash.atencao else CoresDash.bom,
                        legenda = "$foraPrazo fora do prazo de 7 dias"
                    )
                },
                { m ->
                    KpiDash(
                        "Planos de ação abertos", FmtDash.qtd(abertas), m,
                        cor = if (vencidas > 0) CoresDash.ruim else if (abertas > 0) CoresDash.atencao else CoresDash.bom,
                        legenda = "$vencidas vencido(s) · ${acoes.size - abertas} concluído(s)", destaque = vencidas > 0
                    )
                }
            )
            )
            }),
            ItemPainel("periodo", "Ocorrências por período", inteiro = true) { m ->
                GraficoColunas(
                    "Ocorrências por período", "Empilhadas por tipo",
                    faixas.map { it.rotulo },
                    seriesPorChave(semFaixa, faixas, { it.data }, ::nomeTipo, cores),
                    m, grande, fmt = { FmtDash.qtd(it) },
                    selecionada = fFaixa, onClick = { fFaixa = if (fFaixa == it) null else it }
                )
            },
            ItemPainel("caracterizacao", "Por caracterização") { m ->
                GraficoRosca(
                    "Por caracterização", "Pode haver mais de uma marca · clique para filtrar",
                    fatias(contagemCaracterizacao(filtradas), coresRanking(CARACTERIZACOES)),
                    m, grande, selecionada = fCarac, onClick = { fCarac = alternar(fCarac, it) }
                )
            },
            ItemPainel("classe", "Por classificação") { m ->
                GraficoRosca(
                    "Por classificação", "Classe 0 a 5 · clique para filtrar",
                    contagem(filtradas, ::classe).map { (k, v) -> FatiaDash(k, v, corClasseOcorrencia(k)) },
                    m, grande, selecionada = fClasse, onClick = { fClasse = alternar(fClasse, it) }
                )
            },
            ItemPainel("tipo", "Por tipo") { m ->
                GraficoRosca("Por tipo", "Prefixo do registro · clique para filtrar", fatias(porTipo, cores), m, grande, selecionada = fTipo, onClick = { fTipo = alternar(fTipo, it) })
            },
            ItemPainel("tipico", "Típico / Trajeto") { m ->
                GraficoRosca(
                    "Típico / Trajeto", "Clique para filtrar",
                    fatias(contagem(filtradas.filter { it.campo("tipoAcid").isNotBlank() }, ::tipico), coresRanking(listOf("Típico", "Trajeto"))),
                    m, grande, selecionada = fTipico, onClick = { fTipico = alternar(fTipico, it) }
                )
            },
            ItemPainel("obra", "Por obra") { m ->
                RankingBarras("Por obra", "Centro de custo · clique para filtrar", contagem(filtradas, ::obra), m, selecionado = fObra, onClick = { fObra = alternar(fObra, it) })
            },
            ItemPainel("cliente", "Por cliente") { m ->
                RankingBarras("Por cliente", "Clique para filtrar", contagem(filtradas, ::empresa), m, cor = CoresDash.cor(3), selecionado = fEmpresa, onClick = { fEmpresa = alternar(fEmpresa, it) })
            },
            ItemPainel("causas", "Causas da análise") { m ->
                RankingBarras(
                    "Causas da análise", "Fatores marcados como causa na análise dos 6M",
                    causas6m(filtradas), m, cor = CoresDash.cor(5)
                )
            },
            ItemPainel("eficacia", "Eficácia das ações") { m ->
                val eficaz = acoes.count { it.campo("eficacia").equals("Sim", true) }
                val nao = acoes.count { it.campo("eficacia").equals("Não", true) }
                GraficoRosca(
                    "Eficácia das ações", "Verificação do plano de ação",
                    listOf(
                        FatiaDash("Eficaz", eficaz.toDouble(), CoresDash.de("Eficaz", 0xFF1B7F3B)),
                        FatiaDash("Não eficaz", nao.toDouble(), CoresDash.de("Não eficaz", 0xFFC62828)),
                        FatiaDash("Sem verificação", (acoes.size - eficaz - nao).toDouble(), CoresDash.de("Sem verificação", 0xFF8A94A6))
                    ),
                    m, grande, rotuloTotal = "ações"
                )
            },
            ItemPainel("planos", "Planos de ação") { m ->
                GraficoRosca(
                    "Planos de ação", "Prazo das ações das ocorrências filtradas",
                    listOf(
                        FatiaDash("Concluídas", (acoes.size - abertas).toDouble(), CoresDash.de("Concluídas", 0xFF1B7F3B)),
                        FatiaDash("No prazo", (abertas - vencidas).toDouble(), CoresDash.de("No prazo", 0xFFE0A000)),
                        FatiaDash("Vencidas", vencidas.toDouble(), CoresDash.de("Vencidas", 0xFFC62828))
                    ),
                    m, grande, rotuloTotal = "ações"
                )
            },
            ItemPainel("natureza", "Natureza da lesão") { m ->
                RankingBarras(
                    "Natureza da lesão", "Somente quando informada",
                    contagem(filtradas.filter { informada(it.campo("natureza")) }) { it.campo("natureza") },
                    m, cor = CoresDash.cor(2)
                )
            },
            ItemPainel("agente", "Agente causador") { m ->
                RankingBarras(
                    "Agente causador", "Agente da lesão, quando informado",
                    contagem(filtradas.filter { informada(it.campo("agente")) }) { it.campo("agente") }, m, cor = CoresDash.cor(1)
                )
            },
            ItemPainel("corpo", "Partes do corpo", inteiro = true) { m ->
                MapaPartesCorpo(filtradas, ::obra, onObra = { fObra = alternar(fObra, it) }, modifier = m)
            }
        ))
    }
}

private val CARACTERIZACOES = listOf(
    "Ocorrência",
    "Incidente",
    "Acidente ambiental",
    "Acidente sem afastamento",
    "Acidente com afastamento",
    "Acidente c/ danos materiais"
)

private val FATORES_6M = listOf(
    "materialRel" to "Material",
    "metodoRel" to "Método",
    "maquinaRel" to "Máquina",
    "maoObraRel" to "Mão de obra",
    "medidaRel" to "Medida",
    "meioAmbRel" to "Meio ambiente"
)

private fun contagemCaracterizacao(itens: List<Ocorrencia>): List<Pair<String, Double>> {
    val mapa = linkedMapOf<String, Double>()
    itens.forEach { o ->
        o.marcasCaracterizacao().forEach { m -> mapa[m] = (mapa[m] ?: 0.0) + 1.0 }
    }
    return mapa.toList()
}

private fun causas6m(itens: List<Ocorrencia>): List<Pair<String, Double>> {
    val mapa = linkedMapOf<String, Double>()
    itens.forEach { o ->
        val causas = FATORES_6M.filter { (chave, _) -> o.campo(chave).equals("Causa", true) }.map { it.second }
        val lista = if (causas.isNotEmpty()) causas else {
            val raiz = nomeFator(o.campo("causaRaiz"))
            if (raiz.isBlank()) emptyList() else listOf(raiz)
        }
        lista.forEach { mapa[it] = (mapa[it] ?: 0.0) + 1.0 }
    }
    return mapa.toList()
}

private fun nomeFator(raw: String): String = when (raw.trim().lowercase()) {
    "metodo", "método" -> "Método"
    "mao de obra", "mão de obra" -> "Mão de obra"
    "maquina", "máquina" -> "Máquina"
    "material" -> "Material"
    "medida" -> "Medida"
    "meio ambiente" -> "Meio ambiente"
    else -> raw.trim()
}

private fun informada(valor: String): Boolean {
    val t = valor.trim()
    return t.isNotEmpty() && !t.equals("N/A", true) && !t.equals("NA", true)
}

private fun corClasseOcorrencia(rotulo: String): androidx.compose.ui.graphics.Color {
    val n = digitoClasseOc(rotulo)
    val (chave, padrao) = when (n) {
        "0" -> "Classe 0" to 0xFF8A94A6L
        "1" -> "Classe 1" to 0xFFE0A000L
        "2" -> "Classe 2" to 0xFFE07B00L
        "3" -> "Classe 3" to 0xFF6A1B9AL
        "4" -> "Classe 4" to 0xFFC62828L
        "5" -> "Classe 5" to 0xFF6D1212L
        else -> rotulo to 0xFF8A94A6L
    }
    return CoresDash.de(chave, padrao)
}
